// Enlist lists-page kit — the renderer.
//
// A lists page built from theme sections: Favourites, the shopper's lists,
// Create a list, and a side panel. This file renders all of it. Enlist is
// called only through window.qfl.enlist — data (lists(), items(), settings()),
// actions (add / remove / has with a `list`, createList, swapVariant,
// renameList, archiveList) and one event (qfl:wishlist:changed). The contract
// is apps/quick.fire.listings/docs/THEME_INTEGRATION.md, "Building your own
// lists page".
//
// It works on any theme. The only theme-specific piece is the product card,
// which is snippets/enlist-product-card.liquid — swap that file to use your
// theme's own card; see the contract at the top of it.
//
// THE MODEL. If the shop pins a list (Enlist admin → Lists → "Saving from a
// product page always goes to one list"), that list is Favourites: the pool
// every other list is built from. A list's category links show the pool,
// filtered, never the catalogue; finding something new means going to the
// collection page. With no pinned list, Favourites is everything saved.
//
// Hooks are data-enlist-*. They must NOT be data-qfl-*: the Enlist app embed
// loads its own renderers when it finds data-qfl-carousel / data-qfl-outfits /
// data-qfl-create-outfit on a page, and those would fill this markup too.

(() => {
  if (window.__enlistLists) return;           // loaded by several sections
  window.__enlistLists = true;

  const Q = () => (window.qfl && window.qfl.enlist) || null;
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
  const routeRoot = () => (window.Shopify?.routes?.root) || '/';
  const gidTail = (gid) => String(gid || '').split('/').pop();
  const variantGidOf = (id) => `gid://shopify/ProductVariant/${gidTail(id)}`;
  const settings = () => { try { return Q()?.settings?.() || {}; } catch { return {}; } };
  const term = () => (settings().list_term || '').trim() || 'list';
  const categories = () => settings().grouping_collections || [];
  // A setting read off whichever kit section carries it, with a fallback.
  const attr = (sel, name, fallback = '') => {
    const v = $(sel)?.getAttribute(name);
    return v && v.trim() ? v : fallback;
  };
  const buttonClass = (sel) => attr(sel, 'data-enlist-button-class', 'enlist-button');
  const fill = (template, category, lower) => String(template)
    .replace(/\[category\]/g, lower ? String(category || '').toLowerCase() : String(category || ''));
  // From the nearest kit section, per card — so a label rewritten after a
  // size change uses the same prefix as the one first rendered.
  const sizePrefix = (el) => {
    const p = (el?.closest('[data-enlist-size-prefix]')?.getAttribute('data-enlist-size-prefix') || '').trim() || 'Size:';
    return `${p} `;
  };

  function money(cents) {
    const currency = window.Shopify?.currency?.active || 'GBP';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency })
        .format((Number(cents) || 0) / 100);
    } catch { return ((Number(cents) || 0) / 100).toFixed(2); }
  }

  // Theme-neutral feedback. Not qfl.enlist.toast(): that is drawn by Enlist and styled
  // by its stylesheet, which a theme running this kit usually switches off.
  let toastTimer = null;
  function toast(text, kind = 'info') {
    let el = $('.enlist-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'enlist-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.textContent = text;
    el.dataset.kind = kind;
    el.classList.add('enlist-toast--visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('enlist-toast--visible'), 3200);
  }

  // ── The model ────────────────────────────────────────────────
  //
  // ── Sample content, theme editor only ────────────────────────
  //
  // In the theme editor the person editing has usually saved nothing, and
  // every section hides itself when empty, so the page was blank there. With
  // nothing saved AND the editor's sample products on the page
  // (snippets/enlist-sample-data.liquid, which renders only in design mode),
  // the model is two sample lists and a few loose items instead. Nothing is
  // saved while it shows: see SAMPLE_BLOCKED.
  let sampleMode = false;
  let sampleCache;
  function sampleModel() {
    if (!window.Shopify?.designMode) return null;
    if (sampleCache !== undefined) return sampleCache;
    let products = [];
    try { products = JSON.parse($('script[data-enlist-sample]')?.textContent || '[]'); } catch { /* bad island */ }
    if (!products.length) return (sampleCache = null);
    const now = Date.now();
    const at = (i) => new Date(now - i * 60000).toISOString();
    const item = (p, i, qty = 1) => ({ ...p, qty, added_at: at(i) });
    const list = (n, name, picks, contact) => ({
      share_token: `enlist-sample-${n}`, name, can_edit: true, is_primary: false, created_at: at(n),
      extra_fields: contact ? { contact_name: contact } : {},
      items: picks.map(([i, qty]) => products[i] && item(products[i], i, qty)).filter(Boolean),
    });
    // Named with the shop's own term (Enlist settings): "Sample outfit",
    // "Sample project".
    const lists = [
      list(1, `Sample ${term()}`, [[0, 2], [1, 1], [2, 1]], 'Sample contact'),
      list(2, `Another ${term()}`, [[3, 1], [4, 3]]),
    ].filter((l) => l.items.length);
    return (sampleCache = { lists, items: products.map((p, i) => item(p, i)) });
  }
  const SAMPLE_BLOCKED = [
    '[data-enlist-remove]', '[data-enlist-share]', '[data-enlist-share-all]', '[data-enlist-pick]',
    '[data-enlist-panel-add]', '[data-enlist-add-all]', '[data-enlist-create]', '[data-enlist-list-edit]',
  ].join(',');

  // Rebuilt from the bridge on every render rather than patched: lists() and
  // items() are cheap, and state kept here would be a second copy of the
  // app's that could disagree with it.
  function model() {
    let lists = [];
    let items = [];
    try { lists = Q().lists() || []; items = Q().items() || []; } catch { /* not ready */ }
    const sample = !lists.length && !items.length ? sampleModel() : null;
    sampleMode = !!sample;
    if (sample) ({ lists, items } = sample);
    const snap = new Map(items.map((i) => [i.variant_gid, i]));
    const pool = lists.find((l) => l.is_primary) || null;
    // A list the merchant hides by name (lists section setting "Hide the list
    // named"): a shop without pinning often has one default list that every
    // save lands in, which is not a list anyone built and should not show as
    // one. Pinning does this properly; this is for shops that do not pin.
    const hidden = (attr('[data-enlist-lists]', 'data-enlist-hide-list-named') || '').trim().toLowerCase();
    // Newest first; lists older than created_at have none, and go last.
    const others = lists
      .filter((l) => !l.is_primary)
      .filter((l) => !hidden || (l.name || '').trim().toLowerCase() !== hidden)
      .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
    // An item with no product_handle is a deleted or unpublished product:
    // render nothing for it rather than a titleless, priceless card.
    const entriesOf = (list) => (list?.items || [])
      .filter((i) => i.variant_gid)
      .map((i) => ({ ...(snap.get(i.variant_gid) || {}), ...i, qty: i.qty || 1 }))
      .filter((e) => e.product_handle);
    const favourites = pool
      ? entriesOf(pool)
      : items.filter((i) => i.product_handle).map((i) => ({ ...i, qty: i.qty || 1 }));
    return { lists, pool, others, entriesOf, favourites };
  }

  // ── Cards ────────────────────────────────────────────────────
  //
  // Each card is sections/enlist-card.liquid, fetched over the Section
  // Rendering API with ?variant= so it shows the saved variant. Three
  // behaviours carried over from Enlist's own renderer, each learned the hard
  // way:
  //   - at most 6 requests at once: storefronts throttle bursts of section
  //     requests, and a page with 30 saved items is a burst;
  //   - one retry after a short backoff, so a transient 503 still renders;
  //   - "gone" kept apart from "failed". A 404, or a good response with no
  //     card in it, means the product left the storefront. A 429 or a dropped
  //     connection is transient, and treating it as gone would empty a
  //     shopper's lists during a traffic spike.
  // Cached per product AND variant: the same product saved in two sizes is
  // two cards. Also cached per tab, so a return visit is instant.
  const CONCURRENCY = 6;
  let inFlight = 0;
  const waiting = [];
  const acquire = () => (inFlight < CONCURRENCY ? (inFlight += 1, Promise.resolve()) : new Promise((r) => waiting.push(r)));
  const release = () => { const next = waiting.shift(); if (next) next(); else inFlight -= 1; };

  const SS = 'enlist:card:';
  const ssGet = (k) => { try { return sessionStorage.getItem(SS + k); } catch { return null; } };
  const ssSet = (k, html) => {
    try { sessionStorage.setItem(SS + k, html); } catch {
      // Quota, most likely. Drop only this kit's keys; the theme uses it too.
      try {
        const mine = [];
        for (let i = 0; i < sessionStorage.length; i += 1) {
          const key = sessionStorage.key(i);
          if (key && key.startsWith(SS)) mine.push(key);
        }
        mine.forEach((key) => sessionStorage.removeItem(key));
      } catch { /* nothing more to try */ }
    }
  };

  const cardCache = new Map();
  const gone = new Set();
  // section: the card section to render, per section setting "Card section"
  // (data-enlist-card-section). A theme can show a different card in its list
  // rows than in Favourites; both sections must follow enlist-card.liquid's
  // shape (a [data-enlist-card-host] holding a [data-enlist-card]).
  function cardHtml(handle, variantGid, section = 'enlist-card') {
    if (!handle) return Promise.resolve(null);
    const id = gidTail(variantGid);
    const key = `${section}::${handle}::${id}`;
    if (cardCache.has(key)) return cardCache.get(key);
    const cached = ssGet(key);
    if (cached) { const hit = Promise.resolve(cached); cardCache.set(key, hit); return hit; }
    const once = async () => {
      await acquire();
      let res;
      try {
        res = await fetch(`${routeRoot()}products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(section)}${id ? `&variant=${encodeURIComponent(id)}` : ''}`);
      } finally { release(); }
      if (res.status === 404) { gone.add(handle); return null; }
      if (!res.ok) return null;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const host = doc.querySelector('[data-enlist-card-host]');
      if (host && host.querySelector('[data-enlist-card]')) {
        gone.delete(handle);
        ssSet(key, host.outerHTML);
        return host.outerHTML;
      }
      gone.add(handle);
      return null;
    };
    const p = (async () => {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try { const html = await once(); if (html) return html; } catch { /* retry */ }
        if (gone.has(handle)) break;
        if (attempt === 0) await new Promise((r) => setTimeout(r, 400 + Math.floor(Math.random() * 300)));
      }
      cardCache.delete(key);          // a miss is not cached: the next render retries
      return null;
    })();
    cardCache.set(key, p);
    return p;
  }

  // When the card could not be fetched this time (throttled, offline) — never
  // for a product that is gone. Enough to recognise it and reach it.
  function fallbackCard(e) {
    const url = e.product_url || `${routeRoot()}products/${encodeURIComponent(e.product_handle)}`;
    return `<div class="enlist-card" data-enlist-card>
      <div class="enlist-card__media"><a href="${esc(url)}">${e.image_url ? `<img class="enlist-card__img" src="${esc(e.image_url)}" alt="" loading="lazy">` : ''}</a>
        <div class="enlist-card__actions" data-enlist-actions></div></div>
      <div class="enlist-card__info"><a class="enlist-card__title" href="${esc(url)}">${esc(e.product_title || '')}</a>
        ${e.price_cents != null ? `<div class="enlist-card__price"><span>${esc(money(e.price_cents))}</span></div>` : ''}</div>
    </div>`;
  }

  const skeletons = (n) => Array.from({ length: n }, () => `<li class="enlist-skeleton" aria-hidden="true">
      <div class="enlist-skeleton__media"></div><div class="enlist-skeleton__line"></div>
      <div class="enlist-skeleton__line enlist-skeleton__line--short"></div></li>`).join('');

  // Where the kit's buttons go inside a card: a [data-enlist-actions] slot, or
  // the element a card names with data-enlist-actions-selector, or the card.
  function actionsSlot(li) {
    const card = $('[data-enlist-card]', li);
    if (!card) return li;
    const named = card.getAttribute('data-enlist-actions-selector');
    if (named) { try { const el = card.querySelector(named); if (el) return el; } catch { /* bad selector */ } }
    return $('[data-enlist-actions]', card) || card;
  }

  // ── Keyed card lists ─────────────────────────────────────────
  //
  // A card is kept, keyed by its variant, for as long as it is in the list, and
  // only moved when the order changes. Rebuilding cards on every change would
  // re-fetch or re-initialise the theme's card each time, and flash.
  const renderSeq = new WeakMap();
  async function renderCards(ul, entries, decorate) {
    if (!ul) return;
    const seq = (renderSeq.get(ul) || 0) + 1;
    renderSeq.set(ul, seq);
    const existing = new Map($$(':scope > li[data-variant-gid]', ul).map((li) => [li.dataset.variantGid, li]));
    if (!existing.size && entries.length && !ul.dataset.enlistPainted) ul.innerHTML = skeletons(entries.length);

    const section = ul.closest('[data-enlist-card-section]')?.getAttribute('data-enlist-card-section') || 'enlist-card';
    const htmls = await Promise.all(entries.map((e) => (existing.has(e.variant_gid) ? null : cardHtml(e.product_handle, e.variant_gid, section))));
    if (renderSeq.get(ul) !== seq) return;   // a newer render has started; let it win

    const nodes = [];
    entries.forEach((e, i) => {
      let li = existing.get(e.variant_gid);
      if (!li) {
        const html = htmls[i];
        if (!html && gone.has(e.product_handle)) return;
        li = document.createElement('li');
        li.className = 'enlist-item';
        li.dataset.variantGid = e.variant_gid;
        if (e.product_gid) li.dataset.productGid = e.product_gid;
        li.innerHTML = html || fallbackCard(e);
        decorate(li, e);
      }
      nodes.push(li);
    });
    // A tile after the cards, from <template data-enlist-items-end> in the same
    // row or section ("Add more products to this list"). Kept, not rebuilt.
    const endTpl = ul.closest('[data-enlist-list-row], [data-enlist-favourites]')?.querySelector('template[data-enlist-items-end]');
    if (endTpl && nodes.length) {
      let end = ul.querySelector(':scope > li.enlist-items-end');
      if (!end) {
        end = document.createElement('li');
        end.className = 'enlist-items-end';
        end.append(endTpl.content.cloneNode(true));
      }
      nodes.push(end);
    }
    const same = nodes.length === ul.children.length && nodes.every((n, i) => ul.children[i] === n);
    if (!same) ul.replaceChildren(...nodes);
    ul.dataset.enlistPainted = '1';
    markSizes(ul);
  }

  // ── "Size: 32R" ──────────────────────────────────────────────
  //
  // The size of the SAVED variant, from the sizes enlist-card.liquid emits
  // beside every card. Written wherever the card asks for it (see the contract
  // in snippets/enlist-product-card.liquid), and re-written after a shopper
  // picks a different size, because the card is kept rather than refetched.
  function markSizes(scope) {
    $$('li.enlist-item[data-variant-gid]', scope).forEach((li) => {
      const prefix = sizePrefix(li);
      const card = $('[data-enlist-card]', li);
      if (!card) return;
      let sizes = {};
      try { sizes = JSON.parse($('[data-enlist-variant-sizes]', li)?.textContent || '{}'); } catch { /* none */ }
      const size = sizes[gidTail(li.dataset.variantGid)] || null;

      // Highlight among the card's own size buttons, if it names them.
      const btnSel = card.getAttribute('data-enlist-size-button');
      const valAttr = card.getAttribute('data-enlist-size-value-attr');
      if (btnSel && valAttr) {
        try {
          $$(btnSel, card).forEach((b) => b.classList.toggle('enlist-size--saved', !!size && b.getAttribute(valAttr) === size));
        } catch { /* bad selector */ }
      }

      const text = size ? `${prefix}${size}` : '';
      let label = $('[data-enlist-size-label]', card);
      if (!label) {
        const after = card.getAttribute('data-enlist-size-after');
        let anchor = null;
        if (after) { try { anchor = card.querySelector(after); } catch { /* bad selector */ } }
        if (!anchor) return;
        label = anchor.parentElement?.querySelector(':scope > [data-enlist-size-label]');
        if (!label) {
          anchor.insertAdjacentHTML('afterend', '<p class="enlist-card__size" data-enlist-size-label></p>');
          label = anchor.nextElementSibling;
        }
      }
      if (label.textContent !== text) label.textContent = text;
      label.hidden = !text;
    });
  }

  // ── Favourites ───────────────────────────────────────────────
  const FAV = '[data-enlist-favourites]';
  let favSort = 'newness';

  function sortEntries(entries, sort) {
    const out = [...entries];
    if (sort === 'title') out.sort((a, b) => String(a.product_title || '').localeCompare(String(b.product_title || '')));
    else if (sort === 'price') out.sort((a, b) => (a.price_cents || 0) - (b.price_cents || 0));
    else out.sort((a, b) => String(b.added_at || '').localeCompare(String(a.added_at || '')));   // undated last
    return out;
  }

  // A link per category, "Trousers (4) +", to that collection page. A plus
  // means "go and get more", so only the links that leave carry one. Never
  // disabled: a category with nothing saved is when a shopper wants to go
  // and find some.
  function renderCategoryLinks(root, entries) {
    const host = $('[data-enlist-categories]', root);
    if (!host) return;
    const cats = categories();
    host.hidden = !cats.length;
    const html = cats.map((c) => {
      const n = entries.filter((e) => (e.collection_handles || []).includes(c.handle)).length;
      return `<a class="enlist-cat" href="${esc(routeRoot())}collections/${esc(c.handle)}">`
        + `<span class="enlist-cat__label">${esc(c.title || c.handle)} (${n})</span>`
        + '<span class="enlist-cat__plus" aria-hidden="true">+</span></a>';
    }).join('');
    if (host.innerHTML !== html) host.innerHTML = html;
  }

  // Filter buttons: "All" and one per category, narrowing Favourites in place
  // (the category LINKS above leave for the collection page; these do not).
  // A category with nothing saved is not offered: filtering to an empty grid
  // is a dead end.
  let favFilter = 'all';
  function renderFilters(root, all) {
    const host = $('[data-enlist-filters]', root);
    if (!host) return;
    const cats = categories().filter((c) => all.some((e) => (e.collection_handles || []).includes(c.handle)));
    if (favFilter !== 'all' && !cats.some((c) => c.handle === favFilter)) favFilter = 'all';
    host.hidden = cats.length === 0;
    const allLabel = attr(FAV, 'data-enlist-all-label', 'All');
    const btn = (id, label) => `<button type="button" class="enlist-filter${id === favFilter ? ' enlist-filter--active' : ''}" data-enlist-filter="${esc(id)}" aria-pressed="${id === favFilter}">${esc(label)}</button>`;
    const html = btn('all', allLabel) + cats.map((c) => btn(c.handle, c.title || c.handle)).join('');
    if (host.innerHTML !== html) host.innerHTML = html;
  }

  async function renderFavourites(m) {
    const root = $(FAV);
    if (!root) return;
    // "Show: items in no list" (data-enlist-show="unassigned"): only what the
    // shopper saved and has not put into any of their lists yet.
    let pool = m.favourites;
    if (attr(FAV, 'data-enlist-show', 'all') === 'unassigned') {
      const assigned = new Set(m.others.flatMap((l) => (l.items || []).map((i) => i.variant_gid)));
      pool = pool.filter((e) => !assigned.has(e.variant_gid));
    }
    const all = sortEntries(pool, favSort);
    root.hidden = all.length === 0;
    root.dataset.enlistList = m.pool?.share_token || '';
    renderCategoryLinks(root, all);
    renderFilters(root, all);
    const entries = favFilter === 'all' ? all : all.filter((e) => (e.collection_handles || []).includes(favFilter));
    const countEl = $('[data-enlist-count]', root);
    if (countEl) {
      const n = entries.length;
      countEl.textContent = `${n} ${n === 1 ? attr(FAV, 'data-enlist-count-one', 'item') : attr(FAV, 'data-enlist-count-other', 'items')}`;
    }
    const addLabel = attr(FAV, 'data-enlist-add-label', `Add to ${term()}`);
    const cls = buttonClass(FAV);
    // Both switchable: an "Unassigned items" section may leave assigning to
    // the card's own save button and have no remove.
    const withRemove = attr(FAV, 'data-enlist-show-remove', 'true') !== 'false';
    const withPick = attr(FAV, 'data-enlist-show-pick', 'true') !== 'false';
    await renderCards($('[data-enlist-track]', root), entries, (li) => {
      if (withRemove) li.insertAdjacentHTML('beforeend', '<button type="button" class="enlist-remove" data-enlist-remove aria-label="Remove">✕</button>');
      if (withPick) {
        actionsSlot(li).insertAdjacentHTML('beforeend',
          `<button type="button" class="${esc(cls)} enlist-pick" data-enlist-pick>${esc(addLabel)}</button>`);
      }
    });
  }

  // ── The shopper's lists ──────────────────────────────────────
  const LISTS = '[data-enlist-lists]';
  const rows = new Map();

  function renderListLinks(host) {
    if (!host) return;
    const cats = categories();
    host.hidden = !cats.length;
    // The category PICKER: opens the pool, filtered. No count and no plus —
    // those belong to the links that leave for the catalogue.
    const html = cats.map((c) => `<a class="enlist-chip" data-enlist-bubble="${esc(c.handle)}"`
      + ` data-enlist-title="${esc(c.title || c.handle)}" href="${esc(routeRoot())}collections/${esc(c.handle)}">`
      + `${esc(c.title || c.handle)}</a>`).join('');
    if (host.innerHTML !== html) host.innerHTML = html;
  }

  function totals(entries) {
    let total = 0;
    let compare = 0;
    for (const e of entries) {
      const qty = e.qty || 1;
      if (e.price_cents != null) total += e.price_cents * qty;
      compare += (e.compare_at_price_cents != null ? e.compare_at_price_cents : (e.price_cents || 0)) * qty;
    }
    return { total, compare: compare > total ? compare : null };
  }

  async function renderLists(m) {
    const root = $(LISTS);
    if (!root) return;
    const host = $('[data-enlist-lists-host]', root) || root;
    const tpl = $('template[data-enlist-list-template]', root);
    if (!tpl) return;
    // Every list except the pinned one. With nothing pinned that is every
    // list, and Favourites above is everything saved across them.
    const lists = m.others;
    root.hidden = lists.length === 0;

    const work = [];
    for (const l of lists) {
      let row = rows.get(l.share_token);
      if (!row) {
        row = document.createElement('div');
        row.className = 'enlist-list';
        row.setAttribute('data-enlist-list-row', '');
        row.dataset.enlistList = l.share_token;
        row.append(tpl.content.cloneNode(true));
        rows.set(l.share_token, row);
      }
      const entries = m.entriesOf(l);
      const { total, compare } = totals(entries);
      const set = (sel, text) => { const el = $(sel, row); if (el && el.textContent !== text) el.textContent = text; };
      set('[data-enlist-name]', (l.name || '').trim() || `My ${term()}`);
      set('[data-enlist-count]', String(entries.length));
      set('[data-enlist-total]', total ? money(total) : '');
      set('[data-enlist-compare]', compare ? money(compare) : '');
      row.classList.toggle('enlist-list--empty', entries.length === 0);
      const edit = $('[data-enlist-list-edit]', row);
      if (edit) edit.hidden = !l.can_edit;
      renderListLinks($('[data-enlist-bubbles]', row));
      // Theme fields saved on the list (setListFields): <input data-enlist-field
      // ="contact_name">. Filled from extra_fields unless the shopper is typing.
      const extra = l.extra_fields || {};
      $$('[data-enlist-field]', row).forEach((input) => {
        const key = input.getAttribute('data-enlist-field');
        const v = extra[key] == null ? '' : String(extra[key]);
        if (document.activeElement !== input && input.value !== v) input.value = v;
        input.readOnly = !l.can_edit;
      });
      work.push({ row, entries });
    }
    for (const token of [...rows.keys()]) {
      if (!lists.some((l) => l.share_token === token)) rows.delete(token);
    }
    const want = work.map((w) => w.row);
    const same = want.length === host.children.length && want.every((n, i) => host.children[i] === n);
    if (!same) host.replaceChildren(...want);

    const withRemove = attr(LISTS, 'data-enlist-show-remove', 'true') !== 'false';
    await Promise.all(work.map(({ row, entries }) => renderCards($('[data-enlist-items]', row), entries, (li) => {
      if (withRemove) li.insertAdjacentHTML('beforeend', '<button type="button" class="enlist-remove" data-enlist-remove aria-label="Remove">✕</button>');
    })));
    work.forEach(({ row, entries }) => fillQuantities(row, entries));
  }

  // ── How many of each item ────────────────────────────────────
  //
  // A card with a quantity box ([data-enlist-qty], or the theme's own
  // input[name="quantity"]) shows how many the list holds, and a change saves
  // it (setQty) — the list's totals and Add all to cart follow. The kit sets
  // the value and fires `input`, so a theme whose stepper shows the number in
  // another element can copy it across; it saves on `change`, which a theme
  // stepper that sets the value silently must fire itself.
  const QTY = '[data-enlist-qty], input[name="quantity"]';
  //
  // Per box, a small queue: { want, timer, busy }. Clicks only update `want`;
  // a burst of them becomes one save, at most one save per box is in flight,
  // and clicks made during it go in the next save with the latest number. While
  // a box has anything queued or in flight, the list's (older) number is never
  // written back into it — that was the race that ate fast clicks: a save
  // finishing mid-burst reset the box, and the next save sent the old number.
  const QTY_WAIT = 350;
  const qtyQueue = new WeakMap();

  function fillQuantities(row, entries) {
    for (const e of entries) {
      const li = $(`:scope [data-enlist-items] > li[data-variant-gid="${CSS.escape(e.variant_gid)}"]`, row);
      const input = li && $(QTY, li);
      if (!input || qtyQueue.has(input) || document.activeElement === input) continue;
      const v = String(e.qty || 1);
      if (input.value === v) continue;
      input.value = v;
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  function saveQuantity(input) {
    const li = input.closest('li.enlist-item');
    const token = input.closest('[data-enlist-list-row]')?.dataset.enlistList;
    if (!li?.dataset.variantGid || !token) return;
    const q = qtyQueue.get(input) || { want: null, timer: null, busy: false, variantGid: li.dataset.variantGid, token };
    qtyQueue.set(input, q);
    q.want = input.value;
    clearTimeout(q.timer);
    q.timer = setTimeout(() => { q.timer = null; flushQuantity(input, q); }, QTY_WAIT);
  }

  async function flushQuantity(input, q) {
    if (q.busy) return;              // the save in flight picks this up when it ends
    const sent = q.want;
    q.busy = true;
    const r = await Q().setQty({ variantGid: q.variantGid, list: q.token }, sent);
    q.busy = false;
    if (!r?.ok) {
      qtyQueue.delete(input);
      clearTimeout(q.timer);
      toast(attr(LISTS, 'data-enlist-field-error', 'That could not be saved.'), 'error');
      render();                      // back to what the list holds
      return;
    }
    if (q.timer) return;             // more clicks, still settling: their timer flushes
    if (q.want !== sent) { flushQuantity(input, q); return; }
    qtyQueue.delete(input);
  }

  // ── The side panel: the pool, one category ──────────────────
  const PANEL = '[data-enlist-panel]';
  let panelState = null;   // { handle, title, list }

  function openPanel() {
    const el = $(PANEL);
    if (!el) return false;
    el.classList.add('enlist-panel--open');
    el.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('enlist-no-scroll');
    return true;
  }
  function closePanel() {
    const el = $(PANEL);
    if (!el) return;
    el.classList.remove('enlist-panel--open');
    el.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('enlist-no-scroll');
    const body = $('[data-enlist-panel-body]', el);
    if (body) body.replaceChildren();
    panelState = null;
  }

  async function renderPanel() {
    const el = $(PANEL);
    const body = el && $('[data-enlist-panel-body]', el);
    if (!body || !panelState) return;
    const { handle, title, list } = panelState;
    const m = model();
    const entries = m.favourites.filter((e) => (e.collection_handles || []).includes(handle));
    const cls = buttonClass(PANEL);

    // A prompt, not a dead end: say so, and link to the collection.
    if (!entries.length) {
      body.innerHTML = '<div class="enlist-panel__empty">'
        + `<p>${esc(fill(attr(PANEL, 'data-enlist-empty-text', "You haven't saved any [category] yet."), title, true))}</p>`
        + `<a class="${esc(cls)}" href="${esc(routeRoot())}collections/${esc(handle)}">${esc(fill(attr(PANEL, 'data-enlist-browse-label', 'Browse [category]'), title, false))}</a>`
        + '</div>';
      return;
    }
    let ul = $('ul.enlist-panel__grid', body);
    if (!ul || ul.dataset.enlistTarget !== list) {
      body.innerHTML = `<ul class="enlist-panel__grid" data-enlist-target="${esc(list)}" data-enlist-list="${esc(m.pool?.share_token || '')}"></ul>`;
      ul = $('ul.enlist-panel__grid', body);
    }
    await renderCards(ul, entries, (li) => {
      li.insertAdjacentHTML('beforeend', `<button type="button" class="${esc(cls)} enlist-panel__add" data-enlist-panel-add></button>`);
    });
    syncPanelButtons();
  }

  // Which of these the list already holds — from state, every time, so it is
  // right after an add, after a removal behind the panel, and on reopening.
  function syncPanelButtons() {
    const ul = $('ul.enlist-panel__grid');
    if (!ul) return;
    const list = ul.dataset.enlistTarget;
    const addLabel = attr(PANEL, 'data-enlist-add-label', `Add to ${term()}`);
    const addedLabel = attr(PANEL, 'data-enlist-added-label', 'Added');
    $$('li.enlist-item', ul).forEach((li) => {
      const btn = $('[data-enlist-panel-add]', li);
      if (!btn || btn.dataset.enlistBusy === '1') return;
      let held = false;
      try { held = !!Q().has({ variantGid: li.dataset.variantGid, list }); } catch { /* not ready */ }
      btn.textContent = held ? addedLabel : addLabel;
      btn.classList.toggle('enlist-panel__add--added', held);
      btn.disabled = held;
    });
  }

  async function openCategory(handle, title, list) {
    if (!openPanel()) return false;
    const el = $(PANEL);
    const titleEl = $('[data-enlist-panel-title]', el);
    if (titleEl) titleEl.textContent = title || '';
    const body = $('[data-enlist-panel-body]', el);
    if (body) body.replaceChildren();
    panelState = { handle, title, list };
    await renderPanel();
    return true;
  }

  // ── "Add to list" from a Favourites card ─────────────────────
  //
  // An overlay on the card listing the shopper's lists — never the pinned one,
  // where a checkbox could drop an item from the pool while leaving it in the
  // lists — with a way to start a new list.
  function openPicker(li) {
    const variantGid = li?.dataset.variantGid;
    if (!variantGid) return;
    const productGid = li.dataset.productGid || undefined;
    $('.enlist-picker', li)?.remove();
    const t = term();
    const article = /^[aeiou]/i.test(t) ? 'an' : 'a';
    const m = model();
    const rowsData = m.others.map((l) => ({
      list: l.share_token,
      name: (l.name || '').trim() || `My ${t}`,
      initial: !!Q().has({ variantGid, list: l.share_token }),
    }));
    const rowHtml = (r, i) => `
      <label class="enlist-picker__option">
        <input type="checkbox" data-enlist-pick-row="${i}" ${r.initial ? 'checked' : ''}>
        <span>${esc(r.name)}</span>
      </label>`;
    const overlay = document.createElement('div');
    overlay.className = 'enlist-picker';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-label', `Add to ${article} ${t}`);
    overlay.innerHTML = `
      <button type="button" class="enlist-picker__close" data-enlist-pick-close aria-label="Close">✕</button>
      <p class="enlist-picker__title">Add to ${article} ${esc(t)}</p>
      <div class="enlist-picker__options" data-enlist-pick-options>${rowsData.length
    ? rowsData.map(rowHtml).join('')
    : `<p class="enlist-picker__empty">No ${esc(t.toLowerCase())}s yet. Create one below.</p>`}</div>
      <button type="button" class="enlist-link-button" data-enlist-pick-create>+ New ${esc(t)}</button>
      <button type="button" class="${esc(buttonClass(FAV))}" data-enlist-pick-confirm>Done</button>`;
    li.appendChild(overlay);
    li.classList.add('enlist-item--picking');
    $('input, button', overlay)?.focus();

    const close = () => {
      overlay.remove();
      li.classList.remove('enlist-item--picking');
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('click', onOutside, true);
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    const onOutside = (e) => { if (!overlay.contains(e.target)) close(); };
    document.addEventListener('keydown', onKey, true);
    setTimeout(() => document.addEventListener('click', onOutside, true), 0);

    overlay.addEventListener('click', async (e) => {
      if (e.target.closest('[data-enlist-pick-close]')) { close(); return; }
      const create = e.target.closest('[data-enlist-pick-create]');
      if (create) {
        create.disabled = true;
        const r = await Q().createList(`New ${t}`);
        create.disabled = false;
        if (!r?.ok || !r.share_token) { toast('Something went wrong.', 'error'); return; }
        rowsData.push({ list: r.share_token, name: `New ${t}`, initial: false });
        const i = rowsData.length - 1;
        const box = $('[data-enlist-pick-options]', overlay);
        $('.enlist-picker__empty', box)?.remove();
        box.insertAdjacentHTML('beforeend', rowHtml(rowsData[i], i));
        $(`[data-enlist-pick-row="${i}"]`, box).checked = true;
        return;
      }
      const confirm = e.target.closest('[data-enlist-pick-confirm]');
      if (confirm) {
        confirm.disabled = true;
        for (let i = 0; i < rowsData.length; i += 1) {
          const want = !!$(`[data-enlist-pick-row="${i}"]`, overlay)?.checked;
          if (want === rowsData[i].initial) continue;
          // eslint-disable-next-line no-await-in-loop
          if (want) await Q().add({ variantGid, productGid, list: rowsData[i].list });
          // eslint-disable-next-line no-await-in-loop
          else await Q().remove({ variantGid, list: rowsData[i].list });
        }
        close();
      }
    });
  }

  // ── Rename / delete a list ───────────────────────────────────
  function openEdit(row) {
    const dialog = $('[data-enlist-edit-dialog]');
    const list = row?.dataset.enlistList;
    if (!dialog || !list || typeof dialog.showModal !== 'function') return;
    const l = model().lists.find((x) => x.share_token === list);
    if (!l) return;
    dialog.dataset.enlistList = list;
    const input = $('[data-enlist-edit-name]', dialog);
    input.value = (l.name || '').trim();
    $('[data-enlist-edit-error]', dialog).hidden = true;
    $('[data-enlist-edit-confirm]', dialog).hidden = true;
    dialog.showModal();
    input.focus();
    input.select();
  }

  async function saveEdit(dialog) {
    const list = dialog.dataset.enlistList;
    const input = $('[data-enlist-edit-name]', dialog);
    const name = (input.value || '').trim();
    const err = $('[data-enlist-edit-error]', dialog);
    if (!name) { err.textContent = 'Give it a name.'; err.hidden = false; return; }
    const r = await Q().renameList(list, name);
    if (r?.ok) dialog.close();
    else { err.textContent = r?.error || 'Something went wrong.'; err.hidden = false; }
  }

  async function deleteList(dialog) {
    const r = await Q().archiveList(dialog.dataset.enlistList);
    if (r?.ok) dialog.close();
    else {
      const err = $('[data-enlist-edit-error]', dialog);
      err.textContent = r?.error || 'Something went wrong.';
      err.hidden = false;
    }
  }

  // ── Actions ──────────────────────────────────────────────────

  async function removeItem(btn) {
    const li = btn.closest('li.enlist-item');
    const variantGid = li?.dataset.variantGid;
    if (!variantGid) return;
    // The list the card sits in: a list row → that list only; Favourites →
    // the pinned list, which the bridge cascades to every list, because
    // unsaved is unsaved. With nothing pinned, a plain unsave.
    const list = btn.closest('[data-enlist-list]')?.dataset.enlistList || undefined;
    btn.disabled = true;
    await Q().remove(list ? { variantGid, list } : { variantGid });
    if (document.contains(btn)) btn.disabled = false;   // a failed write rolls back
  }

  async function addFromPanel(btn) {
    const li = btn.closest('li.enlist-item');
    const list = btn.closest('[data-enlist-target]')?.dataset.enlistTarget;
    if (!li || !list) return;
    btn.dataset.enlistBusy = '1';
    btn.disabled = true;
    btn.textContent = attr(PANEL, 'data-enlist-added-label', 'Added');
    btn.classList.add('enlist-panel__add--added');
    const r = await Q().add({ variantGid: li.dataset.variantGid, productGid: li.dataset.productGid || undefined, list });
    delete btn.dataset.enlistBusy;
    if (!r?.ok) toast(r?.error || 'Something went wrong.', 'error');
    syncPanelButtons();
  }

  // Everything in the list that can be bought, in one request. A theme with
  // its own cart API gets it through that; otherwise the kit adds through
  // /cart/add.js and fires `enlist:cart:added` — cancel it (preventDefault) to
  // open your own cart drawer instead of going to the cart page.
  async function addListToCart(btn) {
    const token = btn.closest('[data-enlist-list-row]')?.dataset.enlistList;
    const m = model();
    const list = m.lists.find((l) => l.share_token === token);
    const items = m.entriesOf(list)
      .filter((e) => e.available_for_sale !== false)
      .map((e) => ({ id: Number(gidTail(e.variant_gid)), quantity: e.qty || 1 }));
    if (!items.length) return;
    btn.disabled = true;
    try {
      if (window.cart && typeof window.cart.add === 'function') {
        await window.cart.add({ items });
      } else {
        const res = await fetch(`${routeRoot()}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }),
        });
        if (!res.ok) throw new Error(String(res.status));
        const ev = new CustomEvent('enlist:cart:added', { detail: { items }, cancelable: true });
        if (document.dispatchEvent(ev)) window.location.assign(window.Shopify?.routes?.cart_url || `${routeRoot()}cart`);
      }
    } catch (err) {
      console.error('[enlist] add list to cart failed', err);
      toast('Could not add these to your cart.', 'error');
    } finally {
      btn.disabled = false;
    }
  }

  // Share a list: the phone's share sheet where there is one, otherwise copy
  // the link. Always the read-only link (shareUrl never hands out the
  // editable one). A cancelled share sheet is not an error.
  async function shareList(btn) {
    const token = btn.closest('[data-enlist-list-row]')?.dataset.enlistList;
    let url = null;
    try { url = token ? Q().shareUrl(token) : null; } catch { /* old Enlist */ }
    const name = btn.closest('[data-enlist-list-row]')?.querySelector('[data-enlist-name]')?.textContent || document.title;
    await shareLink(url, name);
  }

  // "Share List" for the whole page: one link to every list shown.
  async function shareAllLists() {
    let url = null;
    try { url = Q().shareUrl(model().others.map((l) => l.share_token)); } catch { /* old Enlist */ }
    await shareLink(url, document.title);
  }

  // Prev/next buttons for a row that scrolls: data-enlist-scroll="prev|next",
  // inside the row or section they move.
  function scrollRow(btn) {
    const scope = btn.closest('[data-enlist-list-row], [data-enlist-favourites]');
    const track = scope && $('[data-enlist-track], [data-enlist-items]', scope);
    if (!track) return;
    const card = $(':scope > li', track);
    const step = card ? card.getBoundingClientRect().width + 20 : track.clientWidth * 0.8;
    track.scrollBy({ left: btn.getAttribute('data-enlist-scroll') === 'prev' ? -step : step, behavior: 'smooth' });
  }

  async function shareLink(url, name) {
    if (!url) { toast(attr(LISTS, 'data-enlist-share-error', 'This list cannot be shared.'), 'error'); return; }
    try {
      if (navigator.share) { await navigator.share({ title: name, url }); return; }
    } catch (err) {
      if (err && err.name === 'AbortError') return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast(attr(LISTS, 'data-enlist-copied-label', 'Link copied'));
    } catch {
      window.prompt(attr(LISTS, 'data-enlist-copy-prompt', 'Copy this link'), url);
    }
  }

  async function createList(btn) {
    const root = btn.closest('[data-enlist-create-root]') || document;
    const input = $('[data-enlist-create-name]', root);
    // Without a name field (or with it left blank), the section's "Name for a
    // new list" setting; the shopper renames it afterwards.
    const fallback = (root.getAttribute?.('data-enlist-default-name') || '').trim() || `New ${term()}`;
    const name = (input?.value || '').trim() || fallback;
    btn.disabled = true;
    const r = await Q().createList(name);
    btn.disabled = false;
    if (r?.ok) {
      if (input) input.value = '';
      toast(root.getAttribute?.('data-enlist-created-label') || 'List created.');
    } else {
      toast(r?.error || 'Something went wrong.', 'error');
    }
  }

  // ── A variant picked on a card is saved ──────────────────────
  //
  // A card that lets the shopper change size or colour keeps data-variant-id up
  // to date (the snippet contract). Watched as an attribute, so the kit does
  // not need to know which of the card's controls changed it. Enlist cascades
  // the swap to every list holding the variant: a size is about the product.
  function watchVariantChanges(scope) {
    if (!scope || scope.dataset.enlistWatch === '1') return;
    scope.dataset.enlistWatch = '1';
    new MutationObserver((records) => {
      for (const rec of records) {
        const el = rec.target;
        if (!(el instanceof Element)) continue;
        const li = el.closest('li.enlist-item');
        if (!li) continue;
        const from = li.dataset.variantGid;
        const toId = el.getAttribute('data-variant-id');
        if (!from || !toId) continue;
        const to = variantGidOf(toId);
        if (to === from) continue;
        let list = li.closest('[data-enlist-list]')?.dataset.enlistList;
        if (!list) {
          try { list = (Q().lists() || []).find((l) => (l.items || []).some((i) => i.variant_gid === from))?.share_token; } catch { /* none */ }
        }
        if (!list) continue;
        li.dataset.variantGid = to;
        const productId = el.getAttribute('data-product-id') || gidTail(li.dataset.productGid);
        Q().swapVariant({ list, from, to, productGid: productId || undefined });
        markSizes(li.parentElement);
      }
    }).observe(scope, { subtree: true, attributes: true, attributeFilter: ['data-variant-id'] });
  }

  // ── Render loop ──────────────────────────────────────────────
  //
  // qfl:wishlist:changed fires in bursts — an optimistic write, then the
  // server's answer — so renders are coalesced to one per frame.
  let scheduled = false;
  function render() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(async () => {
      scheduled = false;
      const m = model();
      await Promise.all([renderFavourites(m), renderLists(m)]);
      if (panelState) await renderPanel();
    });
  }

  function wire() {
    document.addEventListener('click', (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (sampleMode && t.closest(SAMPLE_BLOCKED)) {
        e.preventDefault();
        toast(attr(LISTS, 'data-enlist-sample-label', 'Sample content in the theme editor. Nothing is saved.'));
        return;
      }
      const bubble = t.closest('[data-enlist-bubble]');
      if (bubble) {
        e.preventDefault();
        const list = bubble.closest('[data-enlist-list-row]')?.dataset.enlistList || '';
        openCategory(bubble.dataset.enlistBubble, bubble.dataset.enlistTitle || '', list)
          .then((ok) => { if (!ok) window.location.assign(bubble.getAttribute('href')); });
        return;
      }
      const remove = t.closest('[data-enlist-remove]');
      if (remove) { e.preventDefault(); removeItem(remove); return; }
      const filter = t.closest('[data-enlist-filter]');
      if (filter) { e.preventDefault(); favFilter = filter.getAttribute('data-enlist-filter') || 'all'; render(); return; }
      const share = t.closest('[data-enlist-share]');
      if (share) { e.preventDefault(); shareList(share); return; }
      const shareAll = t.closest('[data-enlist-share-all]');
      if (shareAll) { e.preventDefault(); shareAllLists(); return; }
      const scroll = t.closest('[data-enlist-scroll]');
      if (scroll) { e.preventDefault(); scrollRow(scroll); return; }
      const pick = t.closest('[data-enlist-pick]');
      if (pick) { e.preventDefault(); e.stopPropagation(); openPicker(pick.closest('li.enlist-item')); return; }
      const panelAdd = t.closest('[data-enlist-panel-add]');
      if (panelAdd) { e.preventDefault(); addFromPanel(panelAdd); return; }
      const addAll = t.closest('[data-enlist-add-all]');
      if (addAll) { e.preventDefault(); addListToCart(addAll); return; }
      const create = t.closest('[data-enlist-create]');
      if (create) { e.preventDefault(); createList(create); return; }
      const edit = t.closest('[data-enlist-list-edit]');
      if (edit) { e.preventDefault(); openEdit(edit.closest('[data-enlist-list-row]')); return; }
      const dialog = t.closest('[data-enlist-edit-dialog]');
      if (dialog) {
        if (t.closest('[data-enlist-edit-cancel]')) { dialog.close(); return; }
        if (t.closest('[data-enlist-edit-delete]')) { $('[data-enlist-edit-confirm]', dialog).hidden = false; return; }
        if (t.closest('[data-enlist-edit-confirm-no]')) { $('[data-enlist-edit-confirm]', dialog).hidden = true; return; }
        if (t.closest('[data-enlist-edit-confirm-yes]')) { deleteList(dialog); return; }
      }
      if (t.closest('[data-enlist-panel-close]')) closePanel();
    });
    document.addEventListener('submit', (e) => {
      const form = e.target instanceof Element && e.target.closest('[data-enlist-edit-form]');
      if (!form) return;
      e.preventDefault();
      saveEdit(form.closest('[data-enlist-edit-dialog]'));
    });
    // A card's quantity, in a list row, saves to that list.
    document.addEventListener('change', (e) => {
      const input = e.target instanceof Element && e.target.closest(QTY);
      if (sampleMode) return;
      if (input && input.closest('[data-enlist-list-row] li.enlist-item')) saveQuantity(input);
    });
    // A list field (data-enlist-field) saves when the shopper leaves it.
    document.addEventListener('change', (e) => {
      const input = e.target instanceof Element && e.target.closest('[data-enlist-field]');
      if (!input || sampleMode) return;
      const token = input.closest('[data-enlist-list-row]')?.dataset.enlistList;
      if (!token) return;
      const value = input.value.trim();
      Q().setListFields(token, { [input.getAttribute('data-enlist-field')]: value || null })
        .then((r) => { if (!r?.ok) toast(attr(LISTS, 'data-enlist-field-error', 'That could not be saved.'), 'error'); });
    });
    document.addEventListener('change', (e) => {
      const sel = e.target instanceof Element && e.target.closest('[data-enlist-sort]');
      if (!sel) return;
      favSort = sel.value || 'newness';
      render();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && $(PANEL)?.classList.contains('enlist-panel--open')) { closePanel(); return; }
      if (e.key !== 'Enter' || !(e.target instanceof Element) || !e.target.matches('[data-enlist-create-name]')) return;
      const btn = $('[data-enlist-create]', e.target.closest('[data-enlist-create-root]') || document);
      if (btn) { e.preventDefault(); createList(btn); }
    });
    document.addEventListener('qfl:wishlist:changed', render);
    // The theme editor re-renders a section when a setting changes: the rows
    // built from the old template are stale, and the new markup is empty.
    document.addEventListener('shopify:section:load', () => {
      rows.clear();
      [$(FAV), $(LISTS), $(PANEL)].forEach(watchVariantChanges);
      render();
    });
    [$(FAV), $(LISTS), $(PANEL)].forEach(watchVariantChanges);
  }

  let started = false;
  async function start() {
    if (started) return;
    // The bridge methods a lists page needs arrived together; without them
    // (an old Enlist) the page would half-work, so say so and stop. An
    // Enlist from before window.qfl.enlist has only window.QFL.
    if (!Q()) {
      if (window.QFL) console.warn('[enlist] this lists page needs a newer Enlist (window.qfl.enlist missing).');
      return;
    }
    if (typeof Q().createList !== 'function') {
      console.warn('[enlist] this lists page needs a newer Enlist (qfl.enlist.createList missing).');
      return;
    }
    started = true;
    try { await Q().ready; } catch { return; }
    wire();
    render();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('qfl:ready', start);   // the embed may boot after this file
})();
